// /app/frontend/src/pages/InvoiceCheck.jsx
// Stockly — AI Factuurcontrole · directe flow: Factuur → AI-koppeling → Analyse
import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatDate } from "@/components/app/Shared";
import {
  ArrowRight, ArrowLeft, Upload, Camera, ClipboardPaste, Check, Phone,
  RefreshCw, Plus, Trash2, AlertTriangle, MessageSquare, Sparkles, X, RotateCcw,
  CheckCircle2, Search, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const TAG_LABEL = {
  ok: "✓ Correct", missing: "Ontbreekt", extra: "Niet besteld",
  price_up: "Prijs ↑", price_down: "Prijs ↓", qty_mismatch: "Aantal ≠",
};
const TAG_CLASS = {
  ok: "bg-emerald-50 text-emerald-700", missing: "bg-red-50 text-red-700",
  extra: "bg-blue-50 text-blue-700", price_up: "bg-amber-50 text-amber-700",
  price_down: "bg-emerald-50 text-emerald-700", qty_mismatch: "bg-amber-50 text-amber-700",
};

export default function InvoiceCheck() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1 — invoice input
  const [method, setMethod] = useState("upload");
  const [imageB64, setImageB64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pasteText, setPasteText] = useState("");

  // Step 2 — match
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);   // { matched_order, candidates, all_orders, extracted, confidence, reason }
  const [chosenOrder, setChosenOrder] = useState(null);
  const [showAllOrders, setShowAllOrders] = useState(false);

  // Step 3 — analysis
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [camActive, setCamActive] = useState(false);
  const [facing, setFacing] = useState("environment");

  useEffect(() => () => stopCam(), []);

  // ── Camera ──
  const startCam = async () => {
    setError("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: facing } } });
      streamRef.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
      setCamActive(true);
    } catch {
      setError("Camera niet beschikbaar. Gebruik 'Uploaden' of 'Plakken'.");
      setMethod("upload");
    }
  };
  const stopCam = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamActive(false);
  };
  const flipCam = async () => {
    stopCam();
    setFacing(f => f === "environment" ? "user" : "environment");
    setTimeout(startCam, 150);
  };
  const capturePhoto = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    setImagePreview(dataUrl);
    setImageB64(dataUrl.split(",")[1]);
    stopCam();
  };

  // ── Upload ──
  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Upload een afbeelding (JPG, PNG of WEBP)."); return; }
    const reader = new FileReader();
    reader.onload = e => { const full = e.target.result; setImageB64(full.split(",")[1]); setImagePreview(full); };
    reader.readAsDataURL(file);
  };
  const clearImage = () => { setImageB64(null); setImagePreview(null); };

  // ── Step 1 → Step 2 (auto-match) ──
  const goMatch = async () => {
    setStep(2);
    setMatching(true);
    setMatchResult(null);
    setChosenOrder(null);
    setError("");
    try {
      const { data } = await api.post("/invoice_checks/match", {
        source: method,
        image_b64: imageB64 || null,
        raw_text: pasteText || "",
      });
      setMatchResult(data);
      if (data.matched_order) {
        setChosenOrder(data.matched_order);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Kon factuur niet matchen. Probeer opnieuw.");
    }
    setMatching(false);
  };

  // ── Step 2 → Step 3 (analysis) ──
  const runAnalysis = async (orderToUse) => {
    if (!orderToUse) return;
    setStep(3);
    setLoadingAnalysis(true);
    setResult(null);
    setError("");
    const timer = setInterval(() => setLoadStep(s => Math.min(s + 1, 4)), 700);
    try {
      const { data } = await api.post("/invoice_checks/analyze", {
        order_id: orderToUse.id,
        supplier: (orderToUse.suppliers && orderToUse.suppliers[0]) || matchResult?.extracted?.supplier || "",
        source: method,
        image_b64: imageB64 || null,
        raw_text: pasteText || "",
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analyse mislukt. Probeer opnieuw.");
    }
    clearInterval(timer);
    setLoadingAnalysis(false);
    setLoadStep(0);
  };

  const updateStatus = async (status) => {
    if (!result?.check_id) return;
    try {
      await api.patch(`/invoice_checks/${result.check_id}/status`, { status });
      setResult(r => ({ ...r, _status: status }));
      toast.success(status === "approved" ? "Factuur goedgekeurd & geboekt" : "Markering: leverancier wordt gecontacteerd.");
    } catch { toast.error("Status bijwerken mislukt."); }
  };

  const reset = () => {
    setStep(1); setMethod("upload");
    setImageB64(null); setImagePreview(null); setPasteText("");
    setMatchResult(null); setChosenOrder(null); setShowAllOrders(false);
    setResult(null); setError(""); stopCam();
  };

  const canStart = !!imageB64 || pasteText.trim().length > 5;

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="invoice-check-page">
      <Progress step={step} />

      {step === 1 && (
        <Step1Invoice
          method={method} setMethod={(m) => { setMethod(m); stopCam(); }}
          imagePreview={imagePreview} clearImage={clearImage} handleFile={handleFile}
          pasteText={pasteText} setPasteText={setPasteText}
          camActive={camActive} startCam={startCam} stopCam={stopCam} flipCam={flipCam}
          capturePhoto={capturePhoto} videoRef={videoRef} canvasRef={canvasRef}
          error={error} canStart={canStart} onNext={goMatch}
        />
      )}

      {step === 2 && (
        <Step2Match
          matching={matching} matchResult={matchResult} chosenOrder={chosenOrder}
          setChosenOrder={setChosenOrder} showAllOrders={showAllOrders} setShowAllOrders={setShowAllOrders}
          imagePreview={imagePreview}
          onBack={() => setStep(1)}
          onAnalyse={() => runAnalysis(chosenOrder)}
          error={error}
        />
      )}

      {step === 3 && (
        loadingAnalysis ? <Loading loadStep={loadStep} /> :
          error ? <ErrorBox error={error} onBack={() => setStep(2)} /> :
            result ? <Rapport result={result} onApprove={() => updateStatus("approved")} onContact={() => updateStatus("contacted")} onReset={reset} onBack={() => setStep(2)} /> : null
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function Progress({ step }) {
  return (
    <div className="card-flat p-4 flex items-center mb-6">
      {[1, 2, 3].map((n, i) => (
        <div key={n} className={`flex items-center ${i < 2 ? "flex-1" : ""}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
              step > n ? "bg-[#1B2D5C] text-white" : step === n ? "bg-[#5BC044] text-white" : "bg-zinc-200 text-zinc-500"
            }`}>{n}</div>
            <span className={`text-sm font-semibold ${step >= n ? "text-[#1B2D5C]" : "text-zinc-500"}`}>
              {["Factuur", "Koppelen", "Analyse"][i]}
            </span>
          </div>
          {i < 2 && <div className={`flex-1 h-0.5 mx-3 transition-all ${step > n ? "bg-[#1B2D5C]" : "bg-zinc-200"}`} />}
        </div>
      ))}
    </div>
  );
}

function Step1Invoice({
  method, setMethod,
  imagePreview, clearImage, handleFile,
  pasteText, setPasteText,
  camActive, startCam, stopCam, flipCam, capturePhoto, videoRef, canvasRef,
  error, canStart, onNext,
}) {
  return (
    <>
      <div className="card-flat p-6 mb-4">
        <div className="overline mb-4">Factuur invoeren</div>
        <div className="text-sm text-zinc-500 mb-5">
          Upload of fotografeer de factuur. De AI extraheert automatisch de regels én zoekt zelf de bijhorende bestelling.
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { key: "upload", Icon: Upload, label: "Uploaden" },
            { key: "camera", Icon: Camera, label: "Camera" },
            { key: "paste", Icon: ClipboardPaste, label: "Plakken" },
          ].map(({ key, Icon, label }) => (
            <button key={key} onClick={() => setMethod(key)}
              className={`flex flex-col items-center gap-2 p-4 border rounded-md transition-all ${
                method === key ? "border-[#5BC044] bg-emerald-50/40" : "border-zinc-200 hover:border-zinc-300"
              }`} data-testid={`method-${key}-btn`}>
              <Icon size={22} className="text-[#1B2D5C]" strokeWidth={1.7} />
              <span className="text-xs font-semibold text-[#1B2D5C]">{label}</span>
            </button>
          ))}
        </div>

        {method === "upload" && (
          imagePreview ? (
            <ImagePreviewBlock imagePreview={imagePreview} clearImage={clearImage} />
          ) : (
            <label className="block border-2 border-dashed border-zinc-200 rounded-md p-10 text-center cursor-pointer bg-zinc-50 hover:bg-zinc-100/70"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
              data-testid="upload-zone">
              <input type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files[0])} />
              <Upload size={36} className="text-zinc-400 mx-auto mb-3" strokeWidth={1.4} />
              <div className="text-base font-semibold mb-1">Sleep een bestand hierheen</div>
              <div className="text-sm text-zinc-500">of klik · JPG, PNG of WEBP</div>
            </label>
          )
        )}

        {method === "camera" && (
          <div>
            {!camActive && !imagePreview && (
              <button onClick={startCam}
                className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 rounded-full text-sm font-semibold hover:bg-zinc-50"
                data-testid="start-camera-btn">
                <Camera size={15} /> Camera starten
              </button>
            )}
            {camActive && (
              <div className="relative rounded-md overflow-hidden bg-black aspect-[4/3]">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                {[
                  { top: 16, left: 16, classes: "border-t-[3px] border-l-[3px]" },
                  { top: 16, right: 16, classes: "border-t-[3px] border-r-[3px]" },
                  { bottom: 16, left: 16, classes: "border-b-[3px] border-l-[3px]" },
                  { bottom: 16, right: 16, classes: "border-b-[3px] border-r-[3px]" },
                ].map((p, i) => (
                  <div key={i} className={`absolute w-10 h-10 border-emerald-400/80 ${p.classes}`} style={p} />
                ))}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
                  <button onClick={stopCam} className="w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center"><X size={18} /></button>
                  <button onClick={capturePhoto} className="w-14 h-14 rounded-full bg-white border-4 border-white/40" data-testid="capture-btn" />
                  <button onClick={flipCam} className="w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center"><RotateCcw size={18} /></button>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
            )}
            {imagePreview && !camActive && <ImagePreviewBlock imagePreview={imagePreview} clearImage={clearImage} />}
            {error && <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
          </div>
        )}

        {method === "paste" && (
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
            placeholder={"Plak de factuurtekst hier:\nHanos – Factuur 2026/045 – 17-05-2026\nKipfilet 10kg  4 doos  €9,80\nTomaten 5kg  6 kist  €18,50\nTotaal: € 50,80"}
            className="w-full h-36 border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#5BC044] resize-vertical"
            data-testid="paste-textarea" />
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={onNext} disabled={!canStart}
          className="inline-flex items-center gap-2 h-10 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="invoice-next-btn">
          AI: zoek bestelling <Sparkles size={15} />
        </button>
      </div>
    </>
  );
}

function ImagePreviewBlock({ imagePreview, clearImage }) {
  return (
    <div className="relative rounded-md overflow-hidden border-2 border-[#5BC044]">
      <img src={imagePreview} alt="Factuur" className="w-full max-h-72 object-contain" />
      <button onClick={clearImage} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center">
        <X size={14} />
      </button>
    </div>
  );
}

function Step2Match({ matching, matchResult, chosenOrder, setChosenOrder, showAllOrders, setShowAllOrders, imagePreview, onBack, onAnalyse, error }) {
  if (matching) {
    return (
      <div className="card-flat p-12 text-center" data-testid="match-loading">
        <div className="w-12 h-12 mx-auto mb-4 border-[3px] border-zinc-200 border-t-[#5BC044] rounded-full animate-spin" />
        <div className="text-lg font-bold text-[#1B2D5C] mb-1">AI zoekt de bijbehorende bestelling…</div>
        <div className="text-sm text-zinc-500">Leverancier, datum en productregels worden vergeleken met je laatste 30 bestellingen.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-flat p-8" data-testid="match-error">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md mb-4">
          <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800 text-sm">Matching mislukt</div>
            <div className="text-sm text-red-700 mt-0.5">{error}</div>
          </div>
        </div>
        <button onClick={onBack} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 rounded-full text-sm font-semibold hover:bg-zinc-50">
          <ArrowLeft size={15} /> Terug
        </button>
      </div>
    );
  }

  if (!matchResult) return null;

  const { matched_order: matched, candidates, all_orders, extracted, confidence, reason } = matchResult;
  const hasMatch = !!matched;
  const conf = Math.round((confidence || 0) * 100);

  return (
    <div data-testid="match-result">
      {hasMatch && chosenOrder?.id === matched.id && (
        <div className="card-flat p-6 mb-4 border-l-4 border-emerald-500" data-testid="match-confirmed">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <CheckCircle2 size={24} />
            </div>
            <div className="flex-1">
              <div className="overline">AI-koppeling · {conf}% zekerheid</div>
              <div className="text-lg font-bold text-[#1B2D5C] mb-1">Factuur gekoppeld aan bestelling</div>
              <OrderSummary o={matched} />
              {reason && <div className="text-xs text-zinc-500 mt-2 italic">{reason}</div>}
            </div>
          </div>
        </div>
      )}

      {!hasMatch && (
        <div className="card-flat p-6 mb-4 border-l-4 border-amber-500" data-testid="match-not-found">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
              <Search size={22} />
            </div>
            <div className="flex-1">
              <div className="overline">Geen sterke match gevonden</div>
              <div className="text-lg font-bold text-[#1B2D5C] mb-1">Kies handmatig de juiste bestelling</div>
              <div className="text-sm text-zinc-600">
                AI heeft de factuur uitgelezen
                {extracted?.supplier && <> · <b>Leverancier:</b> {extracted.supplier}</>}
                {extracted?.factuurdatum && <> · <b>Datum:</b> {extracted.factuurdatum}</>}
                {extracted?.totaal && <> · <b>Totaal:</b> € {extracted.totaal}</>}
                — maar kon geen bestelling met voldoende zekerheid (≥40%) vinden.
              </div>
              {reason && <div className="text-xs text-zinc-500 mt-2 italic">{reason}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Candidates */}
      {(candidates?.length > 0 || !hasMatch) && (
        <div className="card-flat p-6 mb-4">
          <div className="overline mb-3">{hasMatch ? "Andere kandidaten" : "Top kandidaten"}</div>
          <div className="space-y-2">
            {(candidates || []).map(o => (
              <OrderCard key={o.id} o={o} active={chosenOrder?.id === o.id} onPick={() => setChosenOrder(o)} />
            ))}
            {candidates?.length === 0 && !hasMatch && (
              <div className="text-sm text-zinc-500 py-3">Geen recente bestellingen om uit te kiezen.</div>
            )}
          </div>

          {all_orders?.length > (candidates?.length || 0) && (
            <button onClick={() => setShowAllOrders(s => !s)}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B2D5C] hover:underline"
              data-testid="show-all-orders">
              <ChevronDown size={14} className={showAllOrders ? "rotate-180 transition-transform" : "transition-transform"} />
              {showAllOrders ? "Alle bestellingen verbergen" : `Toon alle ${all_orders.length} recente bestellingen`}
            </button>
          )}
          {showAllOrders && (
            <div className="space-y-2 mt-3 pt-3 border-t border-zinc-100">
              {all_orders.filter(o => !candidates?.some(c => c.id === o.id)).map(o => (
                <OrderCard key={o.id} o={o} active={chosenOrder?.id === o.id} onPick={() => setChosenOrder(o)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invoice preview */}
      {imagePreview && (
        <div className="card-flat p-4 mb-4">
          <div className="overline mb-2">Geüploade factuur</div>
          <img src={imagePreview} alt="Factuur" className="max-h-56 mx-auto" />
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={onBack}
          className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 rounded-full text-sm font-semibold hover:bg-zinc-50">
          <ArrowLeft size={15} /> Andere factuur
        </button>
        <button onClick={onAnalyse} disabled={!chosenOrder}
          className="inline-flex items-center gap-2 h-10 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="analyze-btn">
          {chosenOrder ? "Analyseer factuur" : "Kies eerst een bestelling"} <Sparkles size={15} />
        </button>
      </div>
    </div>
  );
}

function OrderSummary({ o }) {
  return (
    <div className="text-sm text-zinc-700">
      <div className="font-semibold text-[#1B2D5C]">
        {o.restaurant || "—"} · {o.date || "—"} · € {Number(o.total || 0).toFixed(2)}
      </div>
      <div className="text-xs text-zinc-500 mt-0.5">
        {o.n_items} regels · {o.suppliers?.length ? `Leveranciers: ${o.suppliers.join(", ")}` : "geen leverancier"}
      </div>
      {o.top_items?.length > 0 && (
        <div className="text-xs text-zinc-600 mt-1">Top: {o.top_items.slice(0, 3).join(" · ")}</div>
      )}
    </div>
  );
}

function OrderCard({ o, active, onPick }) {
  return (
    <button onClick={onPick}
      className={`w-full text-left p-3 rounded-md border transition-all ${
        active ? "border-[#5BC044] bg-emerald-50/30" : "border-zinc-200 hover:border-[#1B2D5C] hover:bg-zinc-50"
      }`}
      data-testid={`order-pick-${o.id}`}>
      <OrderSummary o={o} />
    </button>
  );
}

const LOAD_LABELS = [
  "Factuurregels uitlezen",
  "Vergelijken met bestelling",
  "Prijzen & totalen controleren",
  "Rapport samenstellen",
];

function Loading({ loadStep }) {
  return (
    <div className="card-flat p-12 text-center">
      <div className="w-12 h-12 mx-auto mb-4 border-[3px] border-zinc-200 border-t-[#5BC044] rounded-full animate-spin" />
      <div className="text-lg font-bold text-[#1B2D5C] mb-5">AI analyseert de factuur…</div>
      <ul className="text-left max-w-xs mx-auto space-y-2.5">
        {LOAD_LABELS.map((l, i) => (
          <li key={i} className={`flex items-center gap-3 text-sm ${
            loadStep > i + 1 ? "text-emerald-600 font-medium" : loadStep === i + 1 ? "text-[#1B2D5C] font-semibold" : "text-zinc-400"
          }`}>
            <div className={`w-4 h-4 rounded-full flex-shrink-0 ${
              loadStep > i + 1 ? "bg-[#5BC044]" : loadStep === i + 1 ? "bg-[#1B2D5C] animate-pulse" : "bg-zinc-200"
            }`} />{l}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ErrorBox({ error, onBack }) {
  return (
    <div className="card-flat p-8">
      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md mb-4">
        <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-red-800 text-sm">Analyse mislukt</div>
          <div className="text-sm text-red-700 mt-0.5">{error}</div>
        </div>
      </div>
      <button onClick={onBack} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 rounded-full text-sm font-semibold hover:bg-zinc-50">
        <ArrowLeft size={15} /> Terug
      </button>
    </div>
  );
}

function Rapport({ result, onApprove, onContact, onReset, onBack }) {
  const { summary = {}, lines = [], totaal = {}, risico, advies } = result;

  const riskStyle = {
    laag:   { bg: "bg-emerald-50 border-emerald-200", icon: "bg-[#5BC044]", text: "De factuur komt overeen met de bestelling." },
    midden: { bg: "bg-amber-50 border-amber-200", icon: "bg-amber-500", text: "Er zijn prijsafwijkingen of ontbrekende producten." },
    hoog:   { bg: "bg-red-50 border-red-200", icon: "bg-red-500", text: "Meerdere ernstige afwijkingen gevonden." },
  }[risico] || { bg: "bg-zinc-50 border-zinc-200", icon: "bg-zinc-400", text: "" };

  const meldingen = lines.filter(l => l.status !== "ok").map(l => {
    const map = {
      missing:      { tone: "red",      msg: `${l.product} ontbreekt op de factuur (besteld: ${l.besteld || "—"}).` },
      price_up:     { tone: "amber",    msg: `${l.product}: prijs gestegen van € ${l.besteld_prijs} → € ${l.factuur_prijs} (${l.verschil_pct}).` },
      price_down:   { tone: "emerald",  msg: `${l.product}: prijs gedaald van € ${l.besteld_prijs} → € ${l.factuur_prijs} (${l.verschil_pct}).` },
      extra:        { tone: "blue",     msg: `${l.product} staat op de factuur maar was niet besteld.` },
      qty_mismatch: { tone: "amber",    msg: `${l.product}: afwijkende hoeveelheid.` },
    };
    return map[l.status] || { tone: "zinc", msg: l.detail || l.product };
  });

  const toneClass = {
    red: "bg-red-50/70 border-l-red-400 text-red-800",
    amber: "bg-amber-50/70 border-l-amber-400 text-amber-800",
    emerald: "bg-emerald-50/70 border-l-emerald-400 text-emerald-800",
    blue: "bg-blue-50/70 border-l-blue-400 text-blue-800",
    zinc: "bg-zinc-50/70 border-l-zinc-300 text-zinc-700",
  };

  return (
    <div data-testid="invoice-result">
      <div className={`flex items-center gap-4 p-5 rounded-md border ${riskStyle.bg} mb-4`}>
        <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white ${riskStyle.icon}`}>
          <AlertTriangle size={22} />
        </div>
        <div>
          <div className="text-xl font-bold text-[#1B2D5C]">
            Risico: {risico ? risico.charAt(0).toUpperCase() + risico.slice(1) : "—"}
          </div>
          <div className="text-sm text-zinc-600 mt-0.5">{riskStyle.text}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[
          { label: "Correct", val: summary.correct, color: "text-emerald-600" },
          { label: "Ontbreekt", val: summary.ontbreekt, color: "text-red-500" },
          { label: "Prijswijziging", val: summary.prijswijziging, color: "text-amber-500" },
          { label: "Niet besteld", val: summary.extra, color: "text-blue-500" },
        ].map(k => (
          <div key={k.label} className="card-flat p-4 text-center">
            <div className={`text-3xl font-bold font-mono ${k.color}`}>{k.val ?? 0}</div>
            <div className="text-xs text-zinc-500 font-semibold mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="card-flat p-6 mb-4">
        <div className="overline mb-3">Meldingen</div>
        {meldingen.length === 0 ? (
          <div className="text-sm text-emerald-700 py-2">✓ Alle producten zijn correct.</div>
        ) : (
          <div className="space-y-2">
            {meldingen.map((m, i) => (
              <div key={i} className={`flex items-start gap-2 p-3 rounded-md border-l-[3px] text-sm ${toneClass[m.tone]}`}>
                {m.msg}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-flat p-6 mb-4">
        <div className="overline mb-3">Producten</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                {["Product", "Besteld", "Factuur", "Verschil", "Status"].map(h => (
                  <th key={h} className="overline pb-2 px-2 border-b border-zinc-100">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-zinc-50">
                  <td className="px-2 py-2 font-semibold">{l.product}</td>
                  <td className="px-2 py-2">{l.besteld ? `${l.besteld}${l.besteld_prijs ? ` · €${l.besteld_prijs}` : ""}` : "—"}</td>
                  <td className="px-2 py-2">{l.factuur_prijs ? `€ ${l.factuur_prijs}` : "—"}</td>
                  <td className={`px-2 py-2 font-mono font-semibold ${
                    String(l.verschil_pct || "").startsWith("+") ? "text-red-500" :
                      String(l.verschil_pct || "").startsWith("-") ? "text-emerald-600" : "text-zinc-400"
                  }`}>{l.verschil_pct || "—"}</td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${TAG_CLASS[l.status] || "bg-zinc-100 text-zinc-600"}`}>
                      {TAG_LABEL[l.status] || l.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-flat p-6 mb-4">
        <div className="overline mb-3">Totaalcontrole</div>
        <div className={`flex items-center justify-between p-4 rounded-md border ${totaal.klopt ? "border-emerald-300 bg-emerald-50/40" : "border-red-300 bg-red-50/40"}`}>
          <div className="flex gap-8">
            <div>
              <div className="text-xs text-zinc-500 mb-0.5">Factuurtotaal</div>
              <div className="font-bold font-mono text-[#1B2D5C]">{totaal.factuur_totaal || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 mb-0.5">Berekend totaal</div>
              <div className="font-bold font-mono text-[#1B2D5C]">{totaal.berekend_totaal || "—"}</div>
            </div>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${totaal.klopt ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
            {totaal.klopt ? "✓ Klopt" : "⚠ Afwijking"}
          </span>
        </div>
        {totaal.detail && <div className="text-sm text-zinc-600 mt-2.5">{totaal.detail}</div>}
      </div>

      {advies && (
        <div className="flex items-start gap-3 p-5 bg-gradient-to-br from-[#1B2D5C]/[0.04] to-[#5BC044]/[0.04] border border-zinc-200 rounded-md mb-4">
          <div className="w-10 h-10 rounded-full bg-[#1B2D5C] text-white flex items-center justify-center flex-shrink-0">
            <MessageSquare size={18} />
          </div>
          <div>
            <div className="overline mb-1">AI Advies</div>
            <div className="text-sm text-[#1B2D5C] leading-relaxed">{advies}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button onClick={onApprove} className="inline-flex items-center gap-2 h-10 px-5 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold" data-testid="approve-btn">
          <Check size={15} /> Goedkeuren & boeken
        </button>
        <button onClick={onContact} className="inline-flex items-center gap-2 h-10 px-5 bg-[#1B2D5C] hover:bg-[#0F1F47] text-white rounded-full text-sm font-semibold" data-testid="contact-btn">
          <Phone size={15} /> Leverancier contacteren
        </button>
        <button onClick={onReset} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 hover:bg-zinc-50 rounded-full text-sm font-semibold">
          <RefreshCw size={15} /> Nieuwe controle
        </button>
        <button onClick={onBack} className="inline-flex items-center gap-2 h-10 px-4 border border-zinc-200 hover:bg-zinc-50 rounded-full text-sm font-semibold">
          <ArrowLeft size={15} /> Terug
        </button>
      </div>
    </div>
  );
}
